import { useField } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React from "react";
import {
  WEGLOT_APPLY_CLASS,
  WEGLOT_SKIP_CLASS,
} from "src/plugins/weglot/constants";
import { FormikFieldValue } from "src/services/formTemplate/answer";
import { BaseInputProps, Option } from "./Question";

const SELECTED_ARCHIVED_STYLE = {
  fontStyle: "italic",
  color: "var(--chakra-colors-gray-500)",
  opacity: 0.7,
};

export interface SingleSelectProps extends BaseInputProps {
  isRequired?: boolean;
  options: Option[];
  onOptionSelected?: (option: Option) => void;
  onSelectionClear?: () => void;
}

export const SingleSelectInput: React.FC<SingleSelectProps> = ({
  id,
  options,
  onOptionSelected,
  onSelectionClear,
  isDisabled = false,
  isRequired = false,
}) => {
  const [field, , helpers] = useField<FormikFieldValue>(id);

  const value = field.value;

  // Find the selected option to check if it's archived
  const selectedOption = React.useMemo(() => {
    return options.find((o) => o.key === value);
  }, [options, value]);
  const isSelectedArchived = selectedOption?.isArchived ?? false;

  // Filter out archived options from dropdown, but keep the selected one if it's archived
  const availableOptions = React.useMemo(() => {
    // Always filter out archived options, except if one is currently selected
    return options.filter((o) => !o.isArchived || o.key === value);
  }, [options, value]);

  React.useEffect(() => {
    if (!onOptionSelected) return;
    for (const option of options) {
      if (option.key === value) {
        onOptionSelected(option);
        continue;
      }
    }
    if (value === "" && onSelectionClear) {
      onSelectionClear();
    }
  }, [value, options, onOptionSelected, onSelectionClear]);
  return (
    <>
      <SelectControl
        isDisabled={isDisabled}
        name={id}
        selectProps={{
          placeholder: "Select answer",
          style: isSelectedArchived ? SELECTED_ARCHIVED_STYLE : undefined,
        }}
        isRequired={isRequired}
        onChange={(event) => {
          // set as touched so it triggers autosave on change instead of on blur.
          helpers.setTouched(true);
          field.onChange(event);
        }}
      >
        {availableOptions.map((o) => {
          const label = o.label ?? o.key;
          return (
            <option
              className={o.translate ? WEGLOT_APPLY_CLASS : WEGLOT_SKIP_CLASS}
              value={o.key}
              key={o.key}
              disabled={o.isArchived}
              style={isSelectedArchived ? SELECTED_ARCHIVED_STYLE : undefined}
            >
              {o.isArchived ? `${label} (archived)` : label}
            </option>
          );
        })}
      </SelectControl>
    </>
  );
};
